import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router-dom'
import { Icon as IconifyIcon } from '@iconify/react'
import ClipLoader from 'react-spinners/ClipLoader'
import moment from 'moment'
import PrivateLayout from '../components/PrivateLayout'
import Footer from '../components/Footer'
import OrganizationSummary from '../components/OrganizationSummary'
import { useLazyGetGrantQuery, useUpdateGrantMutation } from '../store/api/grantApi'
import { FilterOption, GrantData, GrantProgram, GranteeType, UpdateGrantData } from '../types'

const grantProgramOptions: FilterOption[] = [
  {
    label: 'Select Area',
    value: '',
  },
  {
    label: GrantProgram.HumilityInTheology,
    value: GrantProgram.HumilityInTheology,
  },
  {
    label: GrantProgram.CharacterVirtueDevelopment,
    value: GrantProgram.CharacterVirtueDevelopment,
  },
  {
    label: GrantProgram.HumanSciences,
    value: GrantProgram.HumanSciences,
  },
  {
    label: GrantProgram.VirtuesAndWellBeing,
    value: GrantProgram.VirtuesAndWellBeing,
  },
]

const granteeTypeOptions: FilterOption[] = [
  {
    label: 'Select Type',
    value: '',
  },
  {
    label: GranteeType.NonProfitOrganization,
    value: GranteeType.NonProfitOrganization,
  },
  {
    label: GranteeType.EducationalInstitution,
    value: GranteeType.EducationalInstitution,
  },
  {
    label: GranteeType.GovernementAgency,
    value: GranteeType.GovernementAgency,
  },
  {
    label: GranteeType.Individual,
    value: GranteeType.Individual,
  },
]

const requestStatusOptions: FilterOption[] = [
  {
    label: 'Select Status',
    value: '',
  },
  {
    label: 'Open',
    value: 'Open',
  },
  {
    label: 'In Progress',
    value: 'In Progress',
  },
  {
    label: 'Cancelled',
    value: 'Cancelled',
  },
  {
    label: 'Closed',
    value: 'Closed',
  },
]

const EditGrantPage = (): JSX.Element => {
  const navigate = useNavigate()

  const [updateGrant] = useUpdateGrantMutation()
  const [getGrant] = useLazyGetGrantQuery()

  const { grantId } = useParams()
  const [projectTitle, setProjectTitle] = useState<string>('')
  const [programTheme, setProgramTheme] = useState<string>('')
  const [requestStatus, setRequestStatus] = useState<string>('')
  const [summary, setSummary] = useState<string>('')
  const [organizationName, setOrganizationName] = useState<string>('')
  const [organizationType, setOrganizationType] = useState<string>('')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [agreementAmount, setAgreementAmount] = useState<number>(0)
  const [totalAmount, setTotalAmount] = useState<number>(0)

  const [isApiRequestInProgress, setIsApiRequestInProgress] = useState<boolean>(false)
  const [isGetGrantApiRequestInProgress, setIsGetGrantApiRequestInProgress] = useState<boolean>(false)

  const loadData = useCallback(async () => {
    if (grantId) {
      // Get grant details
      try {
        setIsGetGrantApiRequestInProgress(true)

        const grantDetails: GrantData = await getGrant(grantId).unwrap()

        if (grantDetails) {
          setProjectTitle(grantDetails.projectTitle)

          if (grantDetails.programTheme) {
            setProgramTheme(grantDetails.programTheme)
          }

          if (grantDetails.requestStatus) {
            setRequestStatus(grantDetails.requestStatus)
          }

          setSummary(grantDetails.summary)
          setOrganizationName(grantDetails.organizationName)
          setOrganizationType(grantDetails.organizationType)

          if (grantDetails.startDate) {
            setStartDate(moment(grantDetails.startDate).format('YYYY-MM-DD'))
          }

          if (grantDetails.endDate) {
            setEndDate(moment(grantDetails.endDate).format('YYYY-MM-DD'))
          }

          if (grantDetails.agreementAmount) {
            setAgreementAmount(grantDetails.agreementAmount)
          }

          if (grantDetails.totalAmount) {
            setTotalAmount(grantDetails.totalAmount)
          }
        }

        setIsGetGrantApiRequestInProgress(false)
      } catch (e) {
        console.error(e)
        setIsGetGrantApiRequestInProgress(false)
      }
    }
  }, [grantId])

  useEffect(() => {
    loadData()
  }, [loadData])

  const isFormValid = useMemo(() => {
    return (
      projectTitle &&
      programTheme &&
      requestStatus &&
      summary &&
      organizationName &&
      organizationType &&
      startDate &&
      endDate &&
      agreementAmount &&
      totalAmount
    )
  }, [
    projectTitle,
    programTheme,
    requestStatus,
    summary,
    organizationName,
    organizationType,
    startDate,
    endDate,
    agreementAmount,
    totalAmount,
  ])

  const onSaveChangesClicked = useCallback(async () => {
    if (!isFormValid || !grantId) {
      return
    }

    const request: UpdateGrantData = {
      projectTitle,
      programTheme,
      requestStatus,
      summary,
      organizationName,
      organizationType,
      startDate: moment(startDate, 'YYYY-MM-DD').toDate(),
      endDate: moment(endDate, 'YYYY-MM-DD').toDate(),
      agreementAmount,
      totalAmount,
    }

    try {
      setIsApiRequestInProgress(true)
      await updateGrant({ data: request, grantId }).unwrap()

      setIsApiRequestInProgress(false)
      toast('Successfully saved the changes')
      navigate('/grants')
    } catch (e) {
      console.error(e)
      toast.error('Failed to update the grant')
      setIsApiRequestInProgress(false)
    }
  }, [
    isFormValid,
    projectTitle,
    programTheme,
    requestStatus,
    summary,
    organizationName,
    organizationType,
    startDate,
    endDate,
    agreementAmount,
    totalAmount,
  ])

  return (
    <PrivateLayout>
      <div className="content">
        <OrganizationSummary />

        <div className="d-flex align-items-center mb-4">
          <h3>Grant</h3>
          {isGetGrantApiRequestInProgress ? (
            <div className="spinner-wrapper ms-3" style={{ width: 'unset' }}>
              <ClipLoader color="#e5780b" loading={isGetGrantApiRequestInProgress} size={20} />
            </div>
          ) : null}
        </div>

        <div className="row">
          <div className="col-xl-9">
            <form className="row g-3 mb-6">
              <div className="col-12">
                <div className="form-floating">
                  <input
                    className="form-control"
                    id="projectTitle"
                    type="text"
                    placeholder="Title"
                    value={projectTitle}
                    onChange={(e) => {
                      setProjectTitle(e.target.value)
                    }}
                  />
                  <label htmlFor="projectTitle">Project Title</label>
                </div>
              </div>

              <div className="col-12">
                <div className="form-floating">
                  <textarea
                    className="form-control"
                    id="summary"
                    placeholder=""
                    style={{ height: '100px' }}
                    value={summary}
                    onChange={(e) => {
                      setSummary(e.target.value)
                    }}
                  ></textarea>
                  <label htmlFor="summary">Abstract</label>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="form-floating">
                  <select
                    className="form-select"
                    id="organizationType"
                    value={organizationType}
                    onChange={(e) => {
                      setOrganizationType(e.target.value)
                    }}
                  >
                    {granteeTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="organizationType">Grantee Type</label>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="form-floating">
                  <input
                    className="form-control"
                    id="organizationName"
                    type="text"
                    placeholder="Grantee Name"
                    value={organizationName}
                    onChange={(e) => {
                      setOrganizationName(e.target.value)
                    }}
                  />
                  <label htmlFor="organizationName">Grantee Name</label>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="form-floating">
                  <select
                    className="form-select"
                    id="programTheme"
                    value={programTheme}
                    onChange={(e) => {
                      setProgramTheme(e.target.value)
                    }}
                  >
                    {grantProgramOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="programTheme">Grant Area</label>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="flatpickr-input-container">
                  <div className="form-floating">
                    <input
                      className="form-control"
                      id="totalAmount"
                      type="number"
                      placeholder="Project amount"
                      value={totalAmount}
                      onChange={(e) => {
                        setTotalAmount(+e.target.value)
                      }}
                    />
                    <label className="ps-6" htmlFor="totalAmount">
                      Project Amount
                    </label>
                    <IconifyIcon icon="uil:dollar-sign" className="uil uil-dollar-sign flatpickr-icon text-700 me-2" />
                  </div>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="flatpickr-input-container">
                  <div className="form-floating">
                    <input
                      className="form-control"
                      id="agreementAmount"
                      type="number"
                      placeholder="Agreement amount"
                      value={agreementAmount}
                      onChange={(e) => {
                        setAgreementAmount(+e.target.value)
                      }}
                    />
                    <label className="ps-6" htmlFor="agreementAmount">
                      Award Amount
                    </label>
                    <IconifyIcon icon="uil:dollar-sign" className="uil uil-dollar-sign flatpickr-icon text-700 me-2" />
                  </div>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="form-floating">
                  <select
                    className="form-select"
                    id="requestStatus"
                    value={requestStatus}
                    onChange={(e) => {
                      setRequestStatus(e.target.value)
                    }}
                  >
                    {requestStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="requestStatus">Status</label>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="flatpickr-input-container">
                  <div className="form-floating">
                    <input
                      className="form-control datetimepicker"
                      id="startDate"
                      type="date"
                      placeholder="project start"
                      data-options='{"disableMobile":true}'
                      value={startDate}
                      onChange={(e) => {
                        setStartDate(e.target.value)
                      }}
                    />
                    <label className="ps-6" htmlFor="startDate">
                      Project Start
                    </label>
                    <IconifyIcon icon="uil:calendar-alt" className="uil uil-dollar-sign flatpickr-icon text-700 me-2" />
                  </div>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="flatpickr-input-container">
                  <div className="form-floating">
                    <input
                      className="form-control datetimepicker"
                      id="endDate"
                      type="date"
                      placeholder="project end"
                      data-options='{"disableMobile":true}'
                      value={endDate}
                      onChange={(e) => {
                        setEndDate(e.target.value)
                      }}
                    />
                    <label className="ps-6" htmlFor="endDate">
                      Project End
                    </label>
                    <IconifyIcon icon="uil:calendar-alt" className="uil uil-dollar-sign flatpickr-icon text-700 me-2" />
                  </div>
                </div>
              </div>

              <div className="col-12 gy-6">
                <div className="row g-3 justify-content-end">
                  <div className="col-auto">
                    <button
                      type="button"
                      className="btn btn-phoenix-primary px-5"
                      onClick={() => {
                        navigate(-1)
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="col-auto">
                    <button
                      type="button"
                      className="btn btn-primary px-3 px-sm-3"
                      disabled={!isFormValid}
                      style={{ width: '150px' }}
                      onClick={() => {
                        onSaveChangesClicked()
                      }}
                    >
                      {isApiRequestInProgress ? (
                        <ClipLoader color="white" loading={isApiRequestInProgress} size={15} />
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        <Footer />
      </div>
    </PrivateLayout>
  )
}

export default EditGrantPage
